import type { TraceAction, TraceGroup } from '@chatsdet/trace-tools';
import type { CachedAction, PlaybookStep } from '@chatsdet/types';
import type { StoredCompiledEntry } from '../../types.js';
import type { LinkReasonCode } from './decision-log.js';
import {
  cachedActionLiteral,
  discriminatingStepLiteral,
  isCachedActionTraceCompatible,
  isStepTraceCompatible,
  runCodeInteractionMatches,
  stepMatchesCandidate,
  traceActionMethod,
  traceActionRole,
  traceCandidateLiterals,
  type LiteralSignal,
} from './step-linker-candidates.js';
import {
  buildPlaybookStepScope,
  buildRunCodeGroupPartition,
  classifyActionProvenance,
  type CandidateAction,
  type GroupScopeSignal,
} from './step-linker-groups.js';
import { actionIntervalForSteps, solveStepIntervals } from './step-linker-intervals.js';
import {
  classifyLinkerStepAction,
  isLinkableStepAction,
  type CarryThroughChannel,
} from './step-linker-step-policy.js';

export { classifyActionProvenance } from './step-linker-groups.js';

export interface StrictParsedRunCode {
  interactions: Array<{
    kind:
      | 'click'
      | 'dblclick'
      | 'fill'
      | 'type'
      | 'press'
      | 'check'
      | 'uncheck'
      | 'selectOption'
      | 'goto'
      | 'setInputFiles'
      | 'dragTo'
      | 'hover'
      | 'scroll';
    locatorChain?: string;
    value?: string;
    key?: string;
    url?: string;
    underControlFlow: boolean;
    sourceText: string;
  }>;
  hasControlFlow: boolean;
  unclassifiedCount: number;
}

export interface LinkerInput {
  steps: PlaybookStep[];
  segmentActions: Array<{ index: number; action: TraceAction }>;
  groups: TraceGroup[];
  provenance: {
    variableValues: Record<string, string>;
    macroResolutionLog?: Array<{ resolved: string; expression: string }>;
  };
  rowBridge?: {
    compiledEntries: StoredCompiledEntry[];
    actions: CachedAction[];
  };
  parsedRunCodeBySteps?: Map<number, StrictParsedRunCode>;
  runCodeToolCalls?: RunCodeToolCall[];
}

export interface RunCodeToolCall {
  stepOrder: number;
  toolName: 'browser_run_code' | 'browser_evaluate';
}

export interface StepActionLink {
  stepOrder: number;
  actionIndex: number;
  layer: 1 | 2 | 3 | 4;
}

export interface CarryThroughStep {
  stepOrder: number;
  channel: CarryThroughChannel;
}

export interface LinkGap {
  stepOrder: number;
  reason: LinkReasonCode;
  detail?: string;
  signals?: string[];
}

export interface PendingRowDisambiguation {
  stepOrder: number;
  candidateActionIndices: number[];
  signals?: string[];
}

export interface LinkDecisionSignals {
  stepOrder: number;
  signals: string[];
}

export interface LinkResult {
  links: StepActionLink[];
  carryThrough: CarryThroughStep[];
  gaps: LinkGap[];
  pendingRowDisambiguation: PendingRowDisambiguation[];
  signalsByStep?: LinkDecisionSignals[];
}

type Layer = 1 | 2 | 3 | 4;

interface Claim {
  stepOrder: number;
  actionIndex: number;
  layer: Layer;
}

// Local mirror of apps/api/src/workflows/cache/compiler-matchers.ts
// (packages/* cannot import from apps/api).
export const PLAYBOOK_TO_CACHED_ACTION: Record<string, string[]> = {
  click: ['click'],
  fill: ['type', 'fill_form'],
  fill_form: ['fill_form'],
  select: ['select_option'],
  navigate: ['navigate'],
  wait: ['wait_for'],
  scroll: ['scroll', 'evaluate'],
  hover: ['hover'],
  upload: ['file_upload'],
  verify: ['assert'],
  handle_native_dialog: ['handle_dialog'],
  toggle: ['click'],
  press_key: ['press_key'],
  drag: ['drag'],
  resize: ['resize'],
  capture: ['capture', 'evaluate'],
  python_execute: ['python_execute'],
  extract_table: ['extract_table'],
  verify_download: ['assert'],
  verify_download_content: ['assert'],
  screenshot: ['screenshot'],
  agent_loop: [],
  other: [],
};

export function linkStepsToTraceActions(input: LinkerInput): LinkResult {
  const orderedSteps = [...input.steps].sort((a, b) => a.order - b.order);
  const carryThrough: CarryThroughStep[] = [];
  const gapsByStep = new Map<number, LinkGap>();
  const pendingRowDisambiguation: PendingRowDisambiguation[] = [];
  const signalsByStep = new Map<number, Set<string>>();
  const pendingRowSteps = new Set<number>();
  const pendingWeakValue = new Set<number>();
  const pendingAmbiguousValue = new Set<number>();
  const pendingRowUnresolved = new Set<number>();
  const claimsByStep = new Map<number, Claim>();
  const claimStepByAction = new Map<number, number>();

  const addSignal = (stepOrder: number, signal: GroupScopeSignal): void => {
    const signals = signalsByStep.get(stepOrder) ?? new Set<string>();
    signals.add(signal);
    signalsByStep.set(stepOrder, signals);
  };

  const signalsForStep = (stepOrder: number): string[] | undefined => {
    const signals = signalsByStep.get(stepOrder);
    return signals && signals.size > 0 ? [...signals].sort() : undefined;
  };

  const forceGap = (stepOrder: number, reason: LinkReasonCode, detail?: string): void => {
    const existing = claimsByStep.get(stepOrder);
    if (existing) {
      claimsByStep.delete(stepOrder);
      claimStepByAction.delete(existing.actionIndex);
    }
    const signals = signalsForStep(stepOrder);
    gapsByStep.set(
      stepOrder,
      detail
        ? { stepOrder, reason, detail, ...(signals ? { signals } : {}) }
        : { stepOrder, reason, ...(signals ? { signals } : {}) }
    );
  };

  const rememberGap = (stepOrder: number, reason: LinkReasonCode, detail?: string): void => {
    if (!gapsByStep.has(stepOrder) && !claimsByStep.has(stepOrder)) {
      const signals = signalsForStep(stepOrder);
      gapsByStep.set(
        stepOrder,
        detail
          ? { stepOrder, reason, detail, ...(signals ? { signals } : {}) }
          : { stepOrder, reason, ...(signals ? { signals } : {}) }
      );
    }
  };

  const tryClaim = (stepOrder: number, actionIndex: number, layer: Layer): boolean => {
    if (gapsByStep.has(stepOrder)) return false;

    const existingForStep = claimsByStep.get(stepOrder);
    if (existingForStep) {
      if (existingForStep.actionIndex === actionIndex) return true;
      forceGap(
        stepOrder,
        'LINK_LAYER_CONFLICT',
        `layer ${existingForStep.layer} chose action ${existingForStep.actionIndex}; layer ${layer} chose action ${actionIndex}`
      );
      return false;
    }

    const existingStepForAction = claimStepByAction.get(actionIndex);
    if (existingStepForAction !== undefined && existingStepForAction !== stepOrder) {
      forceGap(
        stepOrder,
        'LINK_LAYER_CONFLICT',
        `action ${actionIndex} already claimed by step ${existingStepForAction}`
      );
      return false;
    }

    claimsByStep.set(stepOrder, { stepOrder, actionIndex, layer });
    claimStepByAction.set(actionIndex, stepOrder);
    return true;
  };

  const enforceMonotonicity = (): void => {
    let changed = true;
    while (changed) {
      changed = false;
      const claims = [...claimsByStep.values()].sort((a, b) => a.stepOrder - b.stepOrder);
      for (let i = 0; i < claims.length - 1; i++) {
        const left = claims[i]!;
        const right = claims[i + 1]!;
        if (left.actionIndex < right.actionIndex) continue;
        const drop = left.layer > right.layer ? left : right.layer > left.layer ? right : right;
        forceGap(
          drop.stepOrder,
          'LINK_MONOTONICITY_VIOLATION',
          `claim action ${drop.actionIndex} violates monotonic order`
        );
        changed = true;
        break;
      }
    }
  };

  for (const step of orderedSteps) {
    const policy = classifyLinkerStepAction(step.action);
    if (policy.kind === 'carry-through') {
      carryThrough.push({ stepOrder: step.order, channel: policy.channel });
      continue;
    }
    if (policy.kind === 'agent-only') {
      forceGap(step.order, 'LINK_AGENT_ONLY_STEP');
    }
  }

  const linkableSteps = orderedSteps.filter(
    (step) => isLinkableStepAction(step.action) && !gapsByStep.has(step.order)
  );

  if (linkableSteps.length > 0 && input.segmentActions.length === 0) {
    const reason: LinkReasonCode =
      input.groups.length === 0 ? 'LINK_NO_SEGMENT' : 'LINK_SEGMENT_EMPTY';
    for (const step of linkableSteps) forceGap(step.order, reason);
    return finishResult({
      claimsByStep,
      carryThrough,
      gapsByStep,
      pendingRowDisambiguation,
      signalsByStep,
    });
  }

  const allCandidates = input.segmentActions
    .map((entry): CandidateAction | null => {
      if (classifyActionProvenance(input.groups, entry.action) === 'internal') return null;
      if (entry.action.error) return null;
      const method = traceActionMethod(entry.action);
      return {
        index: entry.index,
        action: entry.action,
        method,
        role: traceActionRole(method),
      };
    })
    .filter((entry): entry is CandidateAction => entry !== null)
    .sort((a, b) => a.index - b.index);

  const playbookScope = buildPlaybookStepScope({
    steps: orderedSteps,
    linkableSteps,
    segmentActions: input.segmentActions,
    groups: input.groups,
    allCandidates,
  });
  if (playbookScope.status === 'malformed') {
    for (const step of linkableSteps) addSignal(step.order, 'group_scope:malformed');
  } else if (playbookScope.status === 'applied') {
    for (const step of linkableSteps) addSignal(step.order, 'group_scope:playbook-step');
  }

  const erroredCandidates = input.segmentActions
    .filter(
      (entry) =>
        entry.action.error && classifyActionProvenance(input.groups, entry.action) !== 'internal'
    )
    .map((entry): CandidateAction => {
      const method = traceActionMethod(entry.action);
      return { index: entry.index, action: entry.action, method, role: traceActionRole(method) };
    });

  const candidateAllowedForStep = (step: PlaybookStep, actionIndex: number): boolean => {
    if (playbookScope.status !== 'applied') return true;
    const allowed = playbookScope.candidateIndicesByStep.get(step.order);
    return allowed ? allowed.has(actionIndex) : true;
  };

  const candidatesForStep = (step: PlaybookStep): CandidateAction[] => {
    if (playbookScope.status !== 'applied') return allCandidates;
    return allCandidates.filter((candidate) => candidateAllowedForStep(step, candidate.index));
  };

  const unclaimedCandidatesForStep = (step: PlaybookStep): CandidateAction[] =>
    candidatesForStep(step).filter((candidate) => !claimStepByAction.has(candidate.index));

  const discriminatingLiterals = new Map<number, LiteralSignal>();
  for (const step of linkableSteps) {
    const signal = discriminatingStepLiteral(step, input.provenance);
    if (signal) discriminatingLiterals.set(step.order, signal);
  }

  // Layer 1: resolved-value exact match.
  for (const step of linkableSteps) {
    if (claimsByStep.has(step.order) || gapsByStep.has(step.order)) continue;
    const signal = discriminatingLiterals.get(step.order);
    if (!signal) continue;
    if (signal.literal.length < 3) {
      pendingWeakValue.add(step.order);
      continue;
    }

    const matches = unclaimedCandidatesForStep(step).filter(
      (candidate) =>
        isStepTraceCompatible(step.action, candidate) &&
        traceCandidateLiterals(candidate.action, signal).includes(signal.literal)
    );
    if (matches.length === 1) {
      tryClaim(step.order, matches[0]!.index, 1);
    } else if (matches.length === 0) {
      forceGap(step.order, 'LINK_VALUE_NOT_IN_TRACE');
    } else if (matches.length > 1) {
      pendingAmbiguousValue.add(step.order);
    }
  }
  enforceMonotonicity();

  // Layer 2: row bridge from compiled cache entries.
  if (input.rowBridge) {
    for (const step of linkableSteps) {
      if (gapsByStep.has(step.order)) continue;
      const entry = input.rowBridge.compiledEntries.find(
        (compiled): compiled is Extract<StoredCompiledEntry, { type: 'cached' }> =>
          compiled.type === 'cached' && compiled.stepOrder === step.order
      );
      if (!entry) continue;

      const cachedActions = entry.actionIndices
        .map((actionIndex) => input.rowBridge?.actions[actionIndex])
        .filter((action): action is CachedAction => action !== undefined);

      if (cachedActions.length !== 1) {
        forceGap(step.order, 'LINK_AMBIGUOUS_CANDIDATES', 'compiled entry is not 1:1');
        continue;
      }

      const cachedAction = cachedActions[0]!;
      const literal = cachedActionLiteral(cachedAction);
      const existingClaim = claimsByStep.get(step.order);
      const candidateSource = existingClaim
        ? candidatesForStep(step)
        : unclaimedCandidatesForStep(step);
      const candidates = candidateSource.filter(
        (candidate) =>
          isStepTraceCompatible(step.action, candidate) &&
          isCachedActionTraceCompatible(cachedAction, candidate) &&
          (!literal || traceCandidateLiterals(candidate.action, literal).includes(literal.literal))
      );

      if (existingClaim) {
        if (candidates.some((candidate) => candidate.index === existingClaim.actionIndex)) {
          continue;
        }
        if (candidates.length > 0) {
          forceGap(
            step.order,
            'LINK_LAYER_CONFLICT',
            `layer ${existingClaim.layer} chose action ${existingClaim.actionIndex}; layer 2 chose action ${candidates[0]!.index}`
          );
        }
        continue;
      }

      if (candidates.length === 0) {
        pendingRowUnresolved.add(step.order);
        continue;
      }

      const valueLessClick =
        step.action === 'click' && cachedAction.actionType === 'click' && literal === null;
      if (valueLessClick) {
        if (candidates.length === 1) {
          tryClaim(step.order, candidates[0]!.index, 2);
        } else if (candidates.length <= 4) {
          pendingRowSteps.add(step.order);
          pendingRowDisambiguation.push({
            stepOrder: step.order,
            candidateActionIndices: candidates.map((candidate) => candidate.index),
          });
        } else {
          forceGap(step.order, 'LINK_TOO_MANY_CANDIDATES');
        }
        continue;
      }

      if (candidates.length === 1) {
        tryClaim(step.order, candidates[0]!.index, 2);
      } else if (candidates.length <= 4) {
        forceGap(step.order, 'LINK_AMBIGUOUS_CANDIDATES');
      } else {
        forceGap(step.order, 'LINK_TOO_MANY_CANDIDATES');
      }
    }
    enforceMonotonicity();
  }

  // Layer 3: static run_code alignment.
  if (input.parsedRunCodeBySteps && input.parsedRunCodeBySteps.size > 0) {
    const runCodePartition = buildRunCodeGroupPartition({
      groups: input.groups,
      segmentActions: input.segmentActions,
      parsedRunCodeBySteps: input.parsedRunCodeBySteps,
      runCodeToolCalls: input.runCodeToolCalls ?? [],
    });
    const parsedStepOrders = new Set(input.parsedRunCodeBySteps.keys());
    const parsedSteps = linkableSteps.filter((step) => parsedStepOrders.has(step.order));

    if (runCodePartition.status === 'join-mismatch') {
      for (const step of parsedSteps) addSignal(step.order, 'group_scope:join-mismatch');
    } else if (runCodePartition.status === 'applied') {
      for (const step of parsedSteps) {
        if (!runCodePartition.actionIndicesByStep.has(step.order)) continue;
        if (
          claimsByStep.has(step.order) ||
          gapsByStep.has(step.order) ||
          pendingRowSteps.has(step.order)
        ) {
          continue;
        }
        addSignal(step.order, 'group_scope:run-code-executed');
        alignRunCodeScopedStep({
          step,
          parsedRunCodeBySteps: input.parsedRunCodeBySteps,
          candidates: unclaimedCandidatesForStep(step).filter(
            (candidate) =>
              candidate.role === 'terminal' &&
              (runCodePartition.actionIndicesByStep.get(step.order)?.has(candidate.index) ?? false)
          ),
          claimsByStep,
          tryClaim,
          forceGap,
        });
      }
    }

    const groupedStepOrders =
      runCodePartition.status === 'applied'
        ? new Set(runCodePartition.actionIndicesByStep.keys())
        : new Set<number>();
    const legacyParsedSteps = parsedSteps.filter((step) => !groupedStepOrders.has(step.order));
    const hasNonlinear = legacyParsedSteps.some((step) => {
      const parsed = input.parsedRunCodeBySteps?.get(step.order);
      return Boolean(
        parsed &&
        (parsed.hasControlFlow ||
          parsed.unclassifiedCount > 0 ||
          parsed.interactions.some((interaction) => interaction.underControlFlow))
      );
    });

    if (hasNonlinear) {
      for (const step of legacyParsedSteps) {
        if (!claimsByStep.has(step.order)) {
          forceGap(step.order, 'LINK_RUNCODE_NONLINEAR');
        }
      }
    } else {
      const blocks = consecutiveBlocks(
        legacyParsedSteps.filter(
          (step) =>
            !claimsByStep.has(step.order) &&
            !gapsByStep.has(step.order) &&
            !pendingRowSteps.has(step.order)
        )
      );
      for (const block of blocks) {
        alignRunCodeBlock({
          block,
          parsedRunCodeBySteps: input.parsedRunCodeBySteps,
          candidatesForStep: (step) =>
            unclaimedCandidatesForStep(step).filter((candidate) => candidate.role === 'terminal'),
          claimsByStep,
          tryClaim,
          forceGap,
        });
      }
    }
    enforceMonotonicity();
  }

  // Layer 4: interval solving between fixed anchors.
  solveStepIntervals({
    steps: linkableSteps.filter(
      (step) =>
        !claimsByStep.has(step.order) &&
        !gapsByStep.has(step.order) &&
        !pendingRowSteps.has(step.order) &&
        !pendingWeakValue.has(step.order)
    ),
    candidatesForStep: unclaimedCandidatesForStep,
    discriminatingLiterals,
    claimsByStep,
    tryClaim,
  });
  enforceMonotonicity();

  for (const step of linkableSteps) {
    if (
      claimsByStep.has(step.order) ||
      gapsByStep.has(step.order) ||
      pendingRowSteps.has(step.order)
    ) {
      continue;
    }
    if (pendingAmbiguousValue.has(step.order)) {
      rememberGap(step.order, 'LINK_AMBIGUOUS_VALUE');
      continue;
    }
    if (pendingWeakValue.has(step.order)) {
      rememberGap(step.order, 'LINK_VALUE_TOO_WEAK');
      continue;
    }
    if (pendingRowUnresolved.has(step.order)) {
      rememberGap(step.order, 'LINK_ROW_SELECTOR_UNRESOLVED');
      continue;
    }
    const compatible = candidatesForStep(step).filter((candidate) =>
      isStepTraceCompatible(step.action, candidate)
    );
    if (
      compatible.length === 0 &&
      erroredCandidates.some(
        (candidate) =>
          candidateAllowedForStep(step, candidate.index) &&
          isStepTraceCompatible(step.action, candidate)
      )
    ) {
      rememberGap(step.order, 'LINK_TARGET_ERRORED');
      continue;
    }
    rememberGap(
      step.order,
      compatible.length === 0 ? 'LINK_NO_MUTATION_FOR_STEP' : 'LINK_AMBIGUOUS_SEQUENCE'
    );
  }

  return finishResult({
    claimsByStep,
    carryThrough,
    gapsByStep,
    pendingRowDisambiguation,
    signalsByStep,
  });
}

export function linkSignalsByStepMap(
  linkResult: Pick<LinkResult, 'signalsByStep'>
): Map<number, string[]> {
  const out = new Map<number, string[]>();
  for (const entry of linkResult.signalsByStep ?? []) {
    out.set(entry.stepOrder, entry.signals);
  }
  return out;
}

function finishResult(args: {
  claimsByStep: Map<number, Claim>;
  carryThrough: CarryThroughStep[];
  gapsByStep: Map<number, LinkGap>;
  pendingRowDisambiguation: PendingRowDisambiguation[];
  signalsByStep: Map<number, Set<string>>;
}): LinkResult {
  const signalsForStep = (stepOrder: number): string[] | undefined => {
    const signals = args.signalsByStep.get(stepOrder);
    return signals && signals.size > 0 ? [...signals].sort() : undefined;
  };
  const signalsByStep = [...args.signalsByStep.entries()]
    .map(([stepOrder, signals]) => ({ stepOrder, signals: [...signals].sort() }))
    .filter((entry) => entry.signals.length > 0)
    .sort((a, b) => a.stepOrder - b.stepOrder);

  return {
    links: [...args.claimsByStep.values()]
      .sort((a, b) => a.stepOrder - b.stepOrder)
      .map((claim) => ({
        stepOrder: claim.stepOrder,
        actionIndex: claim.actionIndex,
        layer: claim.layer,
      })),
    carryThrough: [...args.carryThrough].sort((a, b) => a.stepOrder - b.stepOrder),
    gaps: [...args.gapsByStep.values()]
      .map((gap) => {
        const signals = gap.signals ?? signalsForStep(gap.stepOrder);
        return signals ? { ...gap, signals } : gap;
      })
      .sort((a, b) => a.stepOrder - b.stepOrder),
    pendingRowDisambiguation: [...args.pendingRowDisambiguation]
      .sort((a, b) => a.stepOrder - b.stepOrder)
      .map((pending) => {
        const signals = pending.signals ?? signalsForStep(pending.stepOrder);
        return signals ? { ...pending, signals } : pending;
      }),
    ...(signalsByStep.length > 0 ? { signalsByStep } : {}),
  };
}

function alignRunCodeBlock(args: {
  block: PlaybookStep[];
  parsedRunCodeBySteps: Map<number, StrictParsedRunCode>;
  candidatesForStep: (step: PlaybookStep) => CandidateAction[];
  claimsByStep: Map<number, Claim>;
  tryClaim: (stepOrder: number, actionIndex: number, layer: Layer) => boolean;
  forceGap: (stepOrder: number, reason: LinkReasonCode, detail?: string) => void;
}): void {
  if (args.block.length === 0) return;
  const firstStep = args.block[0]!;
  const lastStep = args.block[args.block.length - 1]!;
  const interval = actionIntervalForSteps(firstStep.order, lastStep.order, args.claimsByStep);
  const intervalCandidatesByStep = new Map<number, CandidateAction[]>();
  for (const step of args.block) {
    intervalCandidatesByStep.set(
      step.order,
      args
        .candidatesForStep(step)
        .filter(
          (candidate) =>
            candidate.index > interval.afterAction && candidate.index < interval.beforeAction
        )
    );
  }
  const intervalCandidates = uniqueCandidates([...intervalCandidatesByStep.values()].flat());

  const interactions = args.block.flatMap((step) => {
    const parsed = args.parsedRunCodeBySteps.get(step.order);
    return (parsed?.interactions ?? []).map((interaction) => ({
      stepOrder: step.order,
      interaction,
    }));
  });

  if (interactions.length === 0) return;

  const matched: Array<{ stepOrder: number; actionIndex: number }> = [];
  let cursor = -Infinity;
  for (const entry of interactions) {
    const stepCandidates = intervalCandidatesByStep.get(entry.stepOrder) ?? [];
    const match = stepCandidates.find(
      (candidate) =>
        candidate.index > cursor &&
        !matched.some((claim) => claim.actionIndex === candidate.index) &&
        runCodeInteractionMatches(entry.interaction, candidate)
    );
    if (!match) {
      for (const step of args.block) args.forceGap(step.order, 'LINK_RUNCODE_PARTIAL_ALIGNMENT');
      return;
    }
    matched.push({ stepOrder: entry.stepOrder, actionIndex: match.index });
    cursor = match.index;
  }

  const matchedIndices = new Set(matched.map((entry) => entry.actionIndex));
  const leftovers = intervalCandidates.filter((candidate) => !matchedIndices.has(candidate.index));
  for (const step of args.block) {
    const stepMatches = matched
      .filter((entry) => entry.stepOrder === step.order)
      .map((entry) => entry.actionIndex)
      .sort((a, b) => a - b);
    if (stepMatches.length < 2) continue;
    const min = stepMatches[0]!;
    const max = stepMatches[stepMatches.length - 1]!;
    const interleaved = intervalCandidates.some(
      (candidate) =>
        candidate.index > min && candidate.index < max && !matchedIndices.has(candidate.index)
    );
    if (interleaved) {
      for (const blockStep of args.block) {
        args.forceGap(blockStep.order, 'LINK_RUNCODE_INTERLEAVED');
      }
      return;
    }
  }

  if (leftovers.length > 0) {
    for (const step of args.block) args.forceGap(step.order, 'LINK_RUNCODE_PARTIAL_ALIGNMENT');
    return;
  }

  for (const step of args.block) {
    const stepMatches = matched.filter((entry) => entry.stepOrder === step.order);
    if (stepMatches.length !== 1) {
      args.forceGap(step.order, 'LINK_RUNCODE_PARTIAL_ALIGNMENT');
      continue;
    }
    args.tryClaim(step.order, stepMatches[0]!.actionIndex, 3);
  }
}

function alignRunCodeScopedStep(args: {
  step: PlaybookStep;
  parsedRunCodeBySteps: Map<number, StrictParsedRunCode>;
  candidates: CandidateAction[];
  claimsByStep: Map<number, Claim>;
  tryClaim: (stepOrder: number, actionIndex: number, layer: Layer) => boolean;
  forceGap: (stepOrder: number, reason: LinkReasonCode, detail?: string) => void;
}): void {
  if (
    args.claimsByStep.has(args.step.order) ||
    args.parsedRunCodeBySteps.get(args.step.order) === undefined
  ) {
    return;
  }

  const interval = actionIntervalForSteps(args.step.order, args.step.order, args.claimsByStep);
  const intervalCandidates = args.candidates.filter(
    (candidate) => candidate.index > interval.afterAction && candidate.index < interval.beforeAction
  );
  const parsed = args.parsedRunCodeBySteps.get(args.step.order)!;
  const nonlinear =
    parsed.hasControlFlow ||
    parsed.unclassifiedCount > 0 ||
    parsed.interactions.some((interaction) => interaction.underControlFlow);

  if (nonlinear) {
    const compatible = intervalCandidates.filter((candidate) =>
      isStepTraceCompatible(args.step.action, candidate)
    );
    if (compatible.length === 1) {
      args.tryClaim(args.step.order, compatible[0]!.index, 3);
      return;
    }
    args.forceGap(args.step.order, 'LINK_RUNCODE_NONLINEAR');
    return;
  }

  const interactions = parsed.interactions;
  if (interactions.length === 0) return;

  const matched: number[] = [];
  let cursor = -Infinity;
  for (const interaction of interactions) {
    const match = intervalCandidates.find(
      (candidate) =>
        candidate.index > cursor &&
        !matched.includes(candidate.index) &&
        runCodeInteractionMatches(interaction, candidate)
    );
    if (!match) {
      args.forceGap(args.step.order, 'LINK_RUNCODE_PARTIAL_ALIGNMENT');
      return;
    }
    matched.push(match.index);
    cursor = match.index;
  }

  const matchedIndices = new Set(matched);
  const leftovers = intervalCandidates.filter((candidate) => !matchedIndices.has(candidate.index));
  if (leftovers.length > 0) {
    args.forceGap(args.step.order, 'LINK_RUNCODE_PARTIAL_ALIGNMENT');
    return;
  }
  if (matched.length !== 1) {
    args.forceGap(args.step.order, 'LINK_RUNCODE_PARTIAL_ALIGNMENT');
    return;
  }
  args.tryClaim(args.step.order, matched[0]!, 3);
}

function uniqueCandidates(candidates: CandidateAction[]): CandidateAction[] {
  const byIndex = new Map<number, CandidateAction>();
  for (const candidate of candidates) {
    if (!byIndex.has(candidate.index)) byIndex.set(candidate.index, candidate);
  }
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}

function consecutiveBlocks(steps: PlaybookStep[]): PlaybookStep[][] {
  const sorted = [...steps].sort((a, b) => a.order - b.order);
  // Playbook order values are persisted data, not a proof of dense numbering.
  // Treat adjacency in the sorted step sequence as the run_code block boundary;
  // any real unparsed action in the interval is still rejected by the total
  // alignment check rather than by fragile order arithmetic.
  return sorted.length > 0 ? [sorted] : [];
}
